import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useResetPasswordMutation } from '../features/auth/authApi';
import InlineError from '../components/InlineError';
import { toast } from 'react-hot-toast';

export default function ResetPassword() {
    const location = useLocation();
    const navigate = useNavigate();
    const [email, setEmail] = useState(location.state?.email || '');
    const [otpCode, setOtpCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetPassword, { isLoading, error }] = useResetPasswordMutation();

    useEffect(() => {
        if (location.state?.email) {
            setEmail(location.state.email);
        }
    }, [location.state]);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        try {
            const result = await resetPassword({ email, otp_code: otpCode, password }).unwrap();
            toast.success('Password reset successfully!');
            // Pass the resolved tenant_slug to login page
            navigate('/login', { 
                state: { 
                    email, 
                    tenant_slug: (result as any)?.tenant_slug 
                }, 
                replace: true 
            });
        } catch (err) {
            // Handled by error state
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center p-6" style={{ background: '#0f172a', fontFamily: "'Inter', sans-serif" }}>
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Create New Password</h1>
                    <p className="mt-2 text-blue-300">Enter the OTP sent to your email and your new password</p>
                </div>

                <form onSubmit={onSubmit} className="space-y-5">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-blue-200 mb-2">
                            Email address
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="otpCode" className="block text-sm font-medium text-blue-200 mb-2">
                            6-Digit OTP Code
                        </label>
                        <input
                            id="otpCode"
                            type="text"
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            className="w-full px-4 py-3 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all text-center text-xl font-bold tracking-widest"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                            placeholder="000000"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-blue-200 mb-2">
                            New Password
                        </label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                            placeholder="••••••••"
                            required
                            minLength={8}
                        />
                    </div>

                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-blue-200 mb-2">
                            Confirm New Password
                        </label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                            placeholder="••••••••"
                            required
                        />
                    </div>

                    <InlineError message={(error as any)?.data?.error?.message || (error as any)?.data?.message || (error as any)?.error} />

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-3 px-4 rounded-xl font-semibold text-white text-base transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90 active:scale-[0.98]"
                        style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                    >
                        {isLoading ? 'Resetting Password...' : 'Reset Password'}
                    </button>
                </form>

                <div className="mt-8 text-center pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <Link to="/forgot-password" className="text-sm text-blue-400 hover:text-white transition-colors">
                        Resend OTP
                    </Link>
                    <span className="mx-3 text-blue-900">|</span>
                    <Link to="/login" className="text-sm text-blue-400 hover:text-white transition-colors">
                        Back to Sign In
                    </Link>
                </div>
            </div>
        </div>
    );
}
